import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTenant } from "../../../Contexts/TenantContext";
import { useAuth } from "../../../Contexts/AuthContext";
import { accountService } from "../../../Api/Services/accountService";
import { formatCnpj, formatCpf, formatPhoneNumber, unformat } from "../../../Utils/DataFormatters";
import { Input } from "../../../Components/Input/Input";
import { CustomerType } from "../../../Enums/CustomerType";
import { toast } from "react-toastify";
import FooterPolicies from "../../../Components/FooterPolicies/FooterPolicies";
import Button from "../../../Components/Button/Button";
import PersonTypeToggle from "../../../Components/PersonTypeToggle/PersonTypeToggle";

const UserCompleteRegistrationPage = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user, logout, updateUser } = useAuth();
  const needsEmail = useMemo(() => !!user?.requiresEmailUpdate, [user]);
  const needsPhone = useMemo(() => !!user?.requiresPhoneUpdate, [user]);
  const userRef = useRef(user);
  const completedRef = useRef(false);
  useEffect(() => { userRef.current = user; }, [user]);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (!completedRef.current && userRef.current?.requiresContactInfoUpdate) {
        void logout({ silent: true });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [customerType, setCustomerType] = useState<CustomerType>(CustomerType.IndividualPerson);
  const [cpf, setCpf] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user && !user.requiresContactInfoUpdate) {
      navigate("/", { replace: true });
    }
  }, [user, navigate, needsEmail, needsPhone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (needsEmail && !email) {
      toast.error("Preencha o e-mail.");
      return;
    }
    if (needsPhone && !phone) {
      toast.error("Preencha o telefone.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = {
        email: needsEmail ? email : undefined,
        phoneNumber: needsPhone ? unformat(phone) : undefined,
      };
      if (cpf || cnpj) {
        payload.customerType = customerType;
        if (customerType === CustomerType.IndividualPerson) {
          payload.cpf = unformat(cpf);
        } else {
          payload.cnpj = unformat(cnpj);
        }
      }
      const refreshedUser = await accountService.completeContactInfo(payload);

      completedRef.current = true;
      updateUser(refreshedUser);
      toast.success("Cadastro completado com sucesso!");
      navigate("/");
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data || "Não foi possível atualizar seus dados.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex justify-center px-4 pt-8">
        <Link to="/">
          {tenant?.logo?.url ? (
            <img
              src={tenant.logo.url}
              alt="Logo"
              className="w-32"
            />
          ) : null}
        </Link>
      </div>

      <div className="flex flex-grow items-center justify-center px-4 py-10">
        <div className="w-full flex justify-center">
          <div className="w-full bg-white max-w-lg border border-gray-200 rounded-lg shadow p-6 sm:p-8">
            <h2 className="text-xl md:text-2xl text-primary-600 font-semibold text-center mb-3 md:mb-6">
              {"COMPLETAR CADASTRO"}
            </h2>
            <p className="text-sm text-gray-600 text-center mb-6">
              {needsEmail && needsPhone && "Precisamos do seu e-mail e telefone reais para liberar seu acesso."}
              {needsEmail && !needsPhone && "Precisamos do seu e-mail real para liberar seu acesso."}
              {needsPhone && !needsEmail && "Precisamos do seu telefone para liberar seu acesso."}
            </p>

            <form className="space-y-4" onSubmit={handleSubmit}>
              {needsEmail && (
                <Input
                  label={"E-mail"}
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              )}

              {needsPhone && (
                <Input
                  label={"Telefone celular"}
                  type="tel"
                  name="phoneNumber"
                  value={formatPhoneNumber(phone)}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(00) 00000-0000"
                  required
                />
              )}

              <div className="border-t border-gray-200 pt-4 mt-2">
                <p className="text-sm text-gray-600 mb-3">{"Para completar seu cadastro, informe seu CPF ou CNPJ:"}</p>
                <PersonTypeToggle value={customerType} onChange={setCustomerType} className="gap-3 mb-4" />
                {customerType === CustomerType.IndividualPerson ? (
                  <Input
                    label="CPF"
                    name="cpf"
                    value={formatCpf(cpf)}
                    onChange={(e) => setCpf(e.target.value)}
                    placeholder="000.000.000-00"
                  />
                ) : (
                  <Input
                    label="CNPJ"
                    name="cnpj"
                    value={formatCnpj(cnpj)}
                    onChange={(e) => setCnpj(e.target.value)}
                    placeholder="00.000.000/0000-00"
                  />
                )}
              </div>

              <Button isLoading={isSubmitting}
                type="submit"
                className="w-full py-3 flex justify-center items-center gap-2"
                disabled={isSubmitting}
              >
                {"SALVAR E CONTINUAR"}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <FooterPolicies />
    </div>
  );
};

export default UserCompleteRegistrationPage;
